import { HStack, PageContainer, PageHeader, Spread, VStack } from '@/components/Atoms'
import { INDETERMINATE } from '@repo/constants'
import { Card, CardContent } from '@/components/Card'
import { Filter } from '@/components/DataTable/FilterControls'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { Note } from '@/components/Note'
import useBrandCodeFilter from '@/hooks/filters/useBrandCodeFilter'
import useBrandFilter from '@/hooks/filters/useBrandFilter'
import useFmcPackSizeStandardFilter from '@/hooks/filters/useFmcPackSizeStandardFilter'
import useMaterialGroupFilter from '@/hooks/filters/useMaterialGroupFilter'
import useMaterialMarkingFilter from '@/hooks/filters/useMaterialMarkingFilter'
import useMaterialStatusFilter from '@/hooks/filters/useMaterialStatusFilter'
import usePackContentSizeFilter from '@/hooks/filters/usePackContentSizeFilter'
import usePriceSegmentFilter from '@/hooks/filters/usePriceSegmentFilter'
import useProductCategoryFilter from '@/hooks/filters/useProductCategoryFilter'
import useProductTypeFilter from '@/hooks/filters/useProductTypeFilter'
import useReleaseStatusFilter from '@/hooks/filters/useReleaseStatusFilter'
import useVariantFilter from '@/hooks/filters/useVariantFilter'
import {
  materialsListExportQueryOptions,
  materialsListSearchSchema,
  useMaterialsList,
} from '@/hooks/queries/crt/useMaterials'
import { useProductTypeMappings } from '@/hooks/queries/crt/useProductTypes'
import { useIncompleteUnreleasedMaterialsCount } from '@/hooks/queries/crt/useUnreleasedMaterials'
import { useTablePagination } from '@/hooks/useTablePagination'
import { getClearedFilters } from '@/lib/utils/filters'
import { queryClient } from '@/main'
import { Button } from '@repo/ui/components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { AlertTriangleIcon, ExternalLinkIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfigurationMenu } from './-components/ConfigurationMenu'
import { MaterialsListDataTable } from './-components/MaterialsListDataTable'

export const Route = createFileRoute('/_auth/_modules/crt/materials/materials-list/')({
  component: RouteComponent,
  validateSearch: (search) => materialsListSearchSchema.parse(search),
})

function RouteComponent() {
  const { t } = useTranslation('materials')
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({
    navigate,
    search,
  })

  const { isPending, error, data } = useMaterialsList({
    ...search,
    page: search.page ?? 1,
    pageSize,
  })

  const { materialGroupFilter, materialGroups } = useMaterialGroupFilter(search.materialGroupCode)
  const { productCategoryFilter, productCategories } = useProductCategoryFilter(
    search.productCategory,
  )
  const { brandFilter, brands } = useBrandFilter(search.brand)
  const { brandCodeFilter, brandCodes } = useBrandCodeFilter(search.brandCode)
  const { variantFilter, variants } = useVariantFilter(search.variant)
  const { packContentSizeFilter, packContentSizes } = usePackContentSizeFilter(
    search.packContentSize,
  )
  const { priceSegmentFilter, priceSegment } = usePriceSegmentFilter(search.priceSegment)
  const { productTypeFilter, productTypes } = useProductTypeFilter(search.productType)
  const { markingFilter, markings } = useMaterialMarkingFilter(search.marking)
  const { statusFilter, status } = useMaterialStatusFilter(search.status)
  const { releaseStatusFilter, releaseStatus } = useReleaseStatusFilter(search.releaseStatus)
  const { fmcPackSizeStandardFilter, isFmcPackSizeStandard } = useFmcPackSizeStandardFilter(
    search.isFmcPackSizeStandard,
  )

  const { data: mappingsData } = useProductTypeMappings()
  const unmappedProductTypesCount = (mappingsData?.results ?? []).filter(
    (row) => row.productTypeId === null && !row.isIgnored,
  ).length

  const { data: incompleteData } = useIncompleteUnreleasedMaterialsCount()
  const incompleteUnreleasedMaterialsCount = incompleteData?.count ?? 0

  const [keyword, setKeyword] = useState<string>(search.keyword ?? '')

  const selectedFilters = {
    materialGroupCode: materialGroups?.length ? materialGroups : undefined,
    productCategory: productCategories?.length ? productCategories : undefined,
    brand: brands?.length ? brands : undefined,
    brandCode: brandCodes?.length ? brandCodes : undefined,
    variant: variants?.length ? variants : undefined,
    packContentSize: packContentSizes?.length ? packContentSizes : undefined,
    priceSegment: priceSegment?.length ? priceSegment : undefined,
    productType: productTypes?.length ? productTypes : undefined,
    marking: markings?.length ? markings : undefined,
    status: status?.length ? status : undefined,
    releaseStatus: releaseStatus?.length ? releaseStatus : undefined,
    isFmcPackSizeStandard:
      isFmcPackSizeStandard !== INDETERMINATE ? isFmcPackSizeStandard : undefined,
  }

  const filters = [
    releaseStatusFilter,
    materialGroupFilter,
    productCategoryFilter,
    brandCodeFilter,
    brandFilter,
    variantFilter,
    packContentSizeFilter,
    priceSegmentFilter,
    productTypeFilter,
    markingFilter,
    statusFilter,
    fmcPackSizeStandardFilter,
  ]

  const showResults = () => {
    navigate({ search: { ...search, ...selectedFilters, keyword: keyword || undefined, page: 1 } })
  }

  const resetAllFilters = () => {
    setKeyword('')
    const clearedFilters = getClearedFilters(selectedFilters)

    navigate({
      search: {
        ...search,
        ...clearedFilters,
        keyword: undefined,
        page: 1,
      },
    })
  }

  const getCsvExport = () => queryClient.ensureQueryData(materialsListExportQueryOptions(search))

  return (
    <PageContainer maxWidth="wide">
      <Spread>
        <PageHeader>{t('materialsListPage.header')}</PageHeader>
        <HStack gap="sm">
          <ExportCsvButton
            getCsvExport={getCsvExport}
            rowCount={+(data?.rowCount ?? 0)}
            label={t('export.viewData', { ns: 'common' })}
          />
          <ConfigurationMenu />
        </HStack>
      </Spread>

      <VStack gap="sm">
        {unmappedProductTypesCount > 0 && (
          <Note
            tone="warning"
            icon={AlertTriangleIcon}
            note={t('materialsListPage.unmappedProductTypes.banner', {
              count: unmappedProductTypesCount,
            })}
            action={
              <PermissionsGuard required={['crt_product_types_edit']}>
                <Button type="button" variant="secondary" size="sm" asChild>
                  <Link to="/crt/materials/data-sources/product-types">
                    <ExternalLinkIcon className="size-4" />
                    {t('materialsListPage.unmappedProductTypes.link')}
                  </Link>
                </Button>
              </PermissionsGuard>
            }
          />
        )}

        {incompleteUnreleasedMaterialsCount > 0 && (
          <Note
            tone="warning"
            icon={AlertTriangleIcon}
            note={t('materialsListPage.unreleasedMaterialsNeedAttention.banner', {
              count: incompleteUnreleasedMaterialsCount,
            })}
            action={
              <PermissionsGuard required={['crt_unreleased_materials_view']}>
                <Button type="button" variant="secondary" size="sm" asChild>
                  <Link to="/crt/materials/data-sources/unreleased-materials">
                    <ExternalLinkIcon className="size-4" />
                    {t('materialsListPage.unreleasedMaterialsNeedAttention.link')}
                  </Link>
                </Button>
              </PermissionsGuard>
            }
          />
        )}
      </VStack>

      <Card>
        <CardContent className="pt-6">
          <MaterialsListDataTable
            filters={filters as Filter<unknown>[]}
            search={{
              value: keyword,
              setValue: setKeyword,
              placeholder: t('materialsListDataTable.searchPlaceholder'),
            }}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            data={data?.results ?? []}
            rowCount={data?.rowCount ?? 0}
            isPending={isPending}
            error={error}
            onShowResults={showResults}
            onResetAllFilters={resetAllFilters}
          />
        </CardContent>
      </Card>
    </PageContainer>
  )
}
